import { Ban, CheckCircle2, FileClock, RotateCcw, UserRound } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { CountBadge } from '../../../../components/CountBadge';
import { ErrorState } from '../../../../components/ErrorState';
import { NextActionAlert } from '../../../../components/NextActionAlert';
import { PreviewTable } from '../../../../components/PreviewTable';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell, ProfileHeaderSkeleton } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { useToast } from '../../../../components/toastContext';
import { usePermission } from '../../../../auth/AuthContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { saveBlob } from '../../../../lib/saveBlob';
import { usePersonNames } from '../../personnel/usePersonNames';
import { NomineeDocumentPreviewModal, UploadNomineeDocumentModal } from '../components/NomineeDocumentModals';
import { NomineeDocumentsCard } from '../components/NomineeDocumentsCard';
import { NomineeModal } from '../components/NomineeModal';
import { NomineeScoresPanel } from '../components/NomineeScoresPanel';
import { ScoreNomineeModal } from '../components/ScoreNomineeModal';
import { nomineeName } from '../nomineeName';
import { nominationsApi } from '../praiseApi';
import type { PraiseSurface } from '../praiseSurface';
import type { AwardDetail, AwardNomineeDetail, AwardNomineeDocument } from '../praiseTypes';

type TabKey = 'info' | 'documents' | 'scores';

type Pending =
  | { kind: 'edit'; award: AwardDetail }
  | { kind: 'delete' }
  | { kind: 'restore' }
  | { kind: 'upload'; document: AwardNomineeDocument }
  | { kind: 'preview'; document: AwardNomineeDocument }
  | { kind: 'score' }
  | null;

/**
 * One nominee, on the header layout, for BOTH surfaces — **Info · Documents**.
 *
 * **It LANDS ON DOCUMENTS**, not Info (still the first declared): a nominee is opened for the papers
 * they submitted — to upload them on Nomination, to read them on Evaluation. **The EMPLOYEE is the
 * title**, the award under it; until the name resolves, or where it is withheld, the award holds it.
 */
export function NomineeProfilePage({ surface }: { surface: PraiseSurface }) {
  const { nomineeId = '' } = useParams<{ nomineeId: string }>();
  const toast = useToast();
  const writes = surface.writes;
  const canReadPersonnel = usePermission('personnel.read');
  // The STAGE results are Evaluation's value — the server strips them on Nomination without this key.
  const canReadEvaluation = usePermission('award-evaluations.read');

  const [nominee, setNominee] = useState<AwardNomineeDetail | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [acting, setActing] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tab, setTab] = useState<TabKey>('documents');

  const personId = nominee?.personId;
  const personIds = useMemo(() => (personId != null ? [personId] : []), [personId]);
  const names = usePersonNames(personIds, surface.api.names);

  const load = useCallback(
    (signal?: AbortSignal) => {
      surface.api
        .getNominee(nomineeId, signal)
        .then((next) => {
          if (!signal?.aborted) {
            setNominee(next);
            setError(null);
          }
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this nominee.', 0));
        });
    },
    [nomineeId, surface],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  const name = nominee ? nomineeName(nominee.personId, names) : null;

  const breadcrumb: BreadcrumbSegment[] = [
    'Human Resources',
    'PRAISE',
    { label: surface.label, to: surface.basePath },
    nominee ? { label: nominee.categoryName, to: `${surface.basePath}/${nominee.awardId}` } : 'Award',
    name ?? 'Nominee',
  ];

  // Guarded on having NOTHING to show. A 403 here is the employee's clearance, and the message says so.
  if (error && !nominee) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (!nominee) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ProfileHeaderSkeleton tabs={2} />
      </div>
    );
  }

  const record = nominee;
  const closedReason = !record.awardIsActive
    ? 'This award is deleted. Restore it before changing its nominees.'
    : !record.isActive
      ? 'This nomination is deleted. Restore it before changing it.'
      : null;

  // Only a RESOLVED name may be the title — "Restricted" in that slot would read as the person's name.
  const resolvedName =
    record.personId != null && names.statusOf(record.personId) !== 'erased' ? (names.nameOf(record.personId) ?? null) : null;

  function saved(next: AwardNomineeDetail) {
    setNominee(next);
    setPending(null);
  }

  // The edit form shows the award's checklist on a create only, but takes the award — fetched on OPEN.
  function openEdit() {
    nominationsApi
      .get(record.awardId)
      .then((award) => setPending({ kind: 'edit', award }))
      .catch((caught: unknown) => toast.failure(caught instanceof ApiError ? caught.message : 'Could not load the award.'));
  }

  async function runConfirmed() {
    if (pending?.kind !== 'delete' && pending?.kind !== 'restore') return;

    setActing(true);
    setActionError(null);

    try {
      if (pending.kind === 'delete') await nominationsApi.removeNominee(record.id);
      else await nominationsApi.restoreNominee(record.id);

      toast.success(`The nomination was ${pending.kind === 'delete' ? 'deleted' : 'restored'}.`);
      setPending(null);
      load();
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setActing(false);
    }
  }

  async function download(document: AwardNomineeDocument) {
    try {
      const { blob } = await surface.api.downloadDocument(record.id, document.id);
      saveBlob(blob, document.fileName ?? document.name);
    } catch (caught) {
      toast.failure(caught instanceof ApiError ? caught.message : 'Could not download that document.');
    }
  }

  const banner = bannerFor(record, writes);

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id={`${surface.key}-nominee-profile`}
        header={
          <ProfileHeader
            id={`${surface.key}-nominee-profile-header`}
            icon={<UserRound className="h-6 w-6" aria-hidden="true" />}
            title={resolvedName ?? record.categoryName}
            meta={resolvedName ? [record.categoryName, record.awardReferenceNumber] : [record.awardReferenceNumber]}
            status={<StatusText tone={record.isActive ? 'active' : 'inactive'}>{record.isActive ? 'Active' : 'Deleted'}</StatusText>}
            createdBy={record.createdByName}
            createdAt={record.createdAt}
            // No Edit here: the Nomination card's cog owns the one modal, and a record offers it once.
            settings={{
              onHistory: () => setHistoryOpen(true),
              onDelete: writes ? () => setPending({ kind: 'delete' }) : undefined,
              deletePermission: 'award-nominations.delete',
              deleteDisabledReason: record.isActive ? null : 'This nomination is already deleted.',
            }}
          />
        }
        banner={
          <NextActionAlert
            error={Boolean(error)}
            id={`${surface.key}-nominee-banner`}
            icon={<banner.icon className="h-4 w-4" aria-hidden="true" />}
            title={error ? 'This page may be out of date' : banner.title}
            description={error ? error.message : banner.description}
            action={
              writes && record.awardIsActive && !record.isActive ? (
                <PermissionGate permission="award-nominations.delete">
                  <Button
                    id="award-nominee-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => setPending({ kind: 'restore' })}
                    disabled={acting}
                  >
                    Restore
                  </Button>
                </PermissionGate>
              ) : null
            }
          />
        }
        tabs={[
          { key: 'info', label: 'Info' },
          {
            key: 'documents',
            label: (
              <>
                Documents <CountBadge count={record.documents.length} />
              </>
            ),
          },
          {
            // The evaluation's result — Evaluation's own key; the server strips it on Nomination without it.
            key: 'scores',
            label: (
              <>
                Scores <CountBadge count={record.scoreSheets.length} />
              </>
            ),
            permission: 'award-evaluations.read',
          },
        ]}
        activeTab={tab}
        onTabChange={(next) => setTab(next as TabKey)}
      >
        {tab === 'scores' ? (
          <NomineeScoresPanel
            surfaceKey={surface.key}
            record={record}
            closedReason={closedReason ?? (record.personId == null ? 'This nominee is above your clearance.' : null)}
            onScore={() => setPending({ kind: 'score' })}
          />
        ) : tab === 'documents' ? (
          <NomineeDocumentsCard
            id={`${surface.key}-nominee-profile-documents`}
            record={record}
            busy={acting}
            onUpload={writes ? (document) => setPending({ kind: 'upload', document }) : undefined}
            onOpen={(document) => setPending({ kind: 'preview', document })}
          />
        ) : (
          <>
            <ProfileCard
              id={`${surface.key}-nominee-profile-nomination`}
              title="Nomination"
              onEdit={writes ? openEdit : undefined}
              editPermission="award-nominations.update"
              editDisabledReason={closedReason ?? (record.personId == null ? 'This nominee is above your clearance.' : null)}
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Employee', value: name },
                  // Personnel's value — the server strips it without the key; the row says nothing then.
                  ...(canReadPersonnel ? [{ label: 'Employee number', value: record.employeeNumber, mono: true }] : []),
                  { label: 'Reference number', value: record.awardReferenceNumber, mono: true },
                  { label: 'Category', value: record.categoryName },
                  { label: 'Remarks', value: record.remarks },
                ]}
              />
            </ProfileCard>

            {canReadEvaluation ? (
              <ProfileCard id={`${surface.key}-nominee-profile-stages`} title="Stage gates">
                <PreviewTable
                  variant="table-display"
                  rows={[
                    {
                      label: 'Requirements qualification',
                      value: record.isQualified == null ? 'Not tagged' : record.isQualified ? 'Qualified' : 'Disqualified',
                    },
                    { label: 'Qualification remarks', value: record.qualificationRemarks ?? null },
                    {
                      label: 'Qualified at',
                      value:
                        record.qualifiedAt != null ? (
                          <time dateTime={toIso(record.qualifiedAt)} className="font-mono">
                            {formatDateTime(record.qualifiedAt)}
                          </time>
                        ) : null,
                    },
                    { label: 'Qualified by', value: record.qualifiedByName ?? null },
                    // The deliberation and the awarding are the AWARD's decisions; an absent one is an em dash, not a "no".
                    { label: 'Deliberation', value: record.isDeliberated ? (record.isWinner ? 'Winner' : 'Not named') : null },
                    { label: 'Final awarding', value: record.isAwarded ? 'Awarded' : null },
                  ]}
                />
              </ProfileCard>
            ) : null}

            {/* Audit is never editable: created and updated are a record of what happened. */}
            <ProfileCard id={`${surface.key}-nominee-profile-audit`} title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Nominated by', value: record.createdByName },
                  {
                    label: 'Nominated',
                    value: (
                      <time dateTime={toIso(record.createdAt)} className="font-mono">
                        {formatDateTime(record.createdAt)}
                      </time>
                    ),
                  },
                  { label: 'Updated by', value: record.updatedByName },
                  {
                    label: 'Updated',
                    value:
                      record.updatedAt != null ? (
                        <time dateTime={toIso(record.updatedAt)} className="font-mono">
                          {formatDateTime(record.updatedAt)}
                        </time>
                      ) : null,
                  },
                ]}
              />
            </ProfileCard>
          </>
        )}
      </ProfileHeaderShell>

      {pending?.kind === 'edit' ? (
        <NomineeModal award={pending.award} editing={record} employeeName={name} onClose={() => setPending(null)} onSaved={saved} />
      ) : null}

      {pending?.kind === 'upload' ? (
        <UploadNomineeDocumentModal
          nominee={record}
          document={pending.document}
          employeeName={resolvedName}
          onClose={() => setPending(null)}
          onSaved={(next) => {
            saved(next);
            toast.success(`${pending.document.name} was submitted.`);
          }}
        />
      ) : null}

      {pending?.kind === 'score' ? (
        <ScoreNomineeModal nominee={record} employeeName={resolvedName} onClose={() => setPending(null)} onSaved={saved} />
      ) : null}

      {pending?.kind === 'preview' ? (
        <NomineeDocumentPreviewModal
          nominee={record}
          document={pending.document}
          download={surface.api.downloadDocument}
          onDownload={() => void download(pending.document)}
          onClose={() => setPending(null)}
        />
      ) : null}

      {pending?.kind === 'delete' || pending?.kind === 'restore' ? (
        <ConfirmDialog
          open
          onClose={() => {
            setPending(null);
            setActionError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={pending.kind === 'delete' ? 'Delete nomination' : 'Restore nomination'}
          danger={pending.kind === 'delete'}
          requireConfirm
          requireCredential={pending.kind === 'delete'}
          loading={acting}
          id={`award-nominee-profile-${pending.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              {pending.kind === 'delete' ? (
                <>
                  <p>
                    {name ?? 'This employee'} is taken off the nominees for {record.categoryName}.
                  </p>
                  <p>Nothing is erased — the documents stay, and the nomination can be restored.</p>
                </>
              ) : (
                <p>
                  {name ?? 'This employee'} returns to the nominees for {record.categoryName}.
                </p>
              )}
              {actionError ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {actionError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}

      {/* The AWARD's trail, scoped to this nominee: a write under it names the award as its parent. */}
      <HistoryLogsModal open={historyOpen} onClose={() => setHistoryOpen(false)} module={surface.module} entityId={record.id} />
    </div>
  );
}

/** One banner for every state, not only the actionable ones. */
function bannerFor(
  record: AwardNomineeDetail,
  writes: boolean,
): { icon: typeof Ban; title: string; description: string;  } {
  if (!record.awardIsActive) {
    return { icon: Ban, title: 'Award deleted', description: 'This nominee’s award is deleted. Restore the award to change anything here.' };
  }

  if (!record.isActive) {
    return { icon: Ban, title: 'Deleted', description: 'This nomination is hidden from the award’s nominees. Restore it to put it back.' };
  }

  const submitted = record.documents.filter((d) => d.fileName != null).length;
  const total = record.documents.length;

  if (submitted === total) {
    return {
      icon: CheckCircle2,
      title: 'Every document submitted',
      description: `All ${total} ${total === 1 ? 'document is' : 'documents are'} in.`,
      
    };
  }

  return {
    icon: FileClock,
    title: `${submitted} of ${total} documents submitted`,
    description: writes
      ? `${total - submitted} ${total - submitted === 1 ? 'document is' : 'documents are'} still to be uploaded on the Documents tab.`
      : `${total - submitted} ${total - submitted === 1 ? 'document has' : 'documents have'} not been submitted yet.`,
    
  };
}
